/**
 * Locale resolution and overlay merging for parsed tours.
 *
 * Pure, allocation-conscious helpers — they only allocate the new branches
 * of a tour that change for a given locale, and never mutate the input.
 */

import type { Tour, TourLocaleOverrides } from './tour-types';

/**
 * Picks which key in `tour.locales` matches the Experience app locale
 * (BCP-47). Tries the full tag (lowercased), then the primary language
 * subtag.
 *
 * @example pickTourLocaleKey("es-ES", { es: { title: "…" } }) → "es"
 */
export function pickTourLocaleKey(
  appLocale: string | null | undefined,
  locales: Record<string, TourLocaleOverrides> | undefined,
): string | undefined {
  if (!appLocale || !locales) return undefined;
  // Direct hash lookups — no need to materialize Object.keys for membership.
  const lower = appLocale.trim().toLowerCase().replace(/_/g, '-');
  if (lower.length === 0) return undefined;
  if (Object.prototype.hasOwnProperty.call(locales, lower)) return lower;
  const dash = lower.indexOf('-');
  if (dash !== -1) {
    const primary = lower.slice(0, dash);
    if (Object.prototype.hasOwnProperty.call(locales, primary)) return primary;
  }
  return undefined;
}

/**
 * Merges a parsed overlay into `tour.locales[localeKey]`.
 *
 * Returns a new (non-frozen) Tour object — note that consumers that need
 * the deep-frozen invariant must re-run {@link validateTour} afterwards.
 */
export function attachLocaleOverlay(
  tour: Tour,
  localeKey: string,
  overlay: TourLocaleOverrides,
): Tour {
  const merged: Tour = {
    ...tour,
    locales: {
      ...(tour.locales ?? {}),
      [localeKey]: overlay,
    },
  };
  return merged;
}

/**
 * Returns a new Tour with localizable strings replaced from
 * `tour.locales[locale]`. Falls back to the base tour when `locale` is
 * undefined or missing from `locales`.
 *
 * The returned tour shares step references with the base tour for steps
 * that have no overlay, so callers can rely on `===` for cache keys.
 */
export function applyLocale(tour: Tour, locale: string | undefined): Tour {
  if (!locale) return tour;
  const ov = tour.locales?.[locale];
  if (!ov) return tour;

  const baseSteps = tour.steps;
  const ovSteps = ov.steps;
  let steps = baseSteps;

  if (ovSteps && ovSteps.length > 0) {
    steps = baseSteps.map((s, i) => {
      const stepOv = ovSteps[i];
      if (!stepOv?.narrate) return s;
      if (s.type === 'narrate') return { ...s, text: stepOv.narrate };
      return { ...s, narrate: stepOv.narrate };
    });
  }

  return {
    ...tour,
    title: ov.title ?? tour.title,
    summary: ov.summary ?? tour.summary,
    steps,
  };
}
